/// SplitPane: Resizable split pane supporting horizontal and vertical splits.
/// Draggable divider with min/max constraints and collapse behavior.

import { useRef, useState, useCallback, type ReactNode } from "react";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SplitPaneProps {
  /** Split direction. */
  direction: "horizontal" | "vertical";
  /** First pane content. */
  first: ReactNode;
  /** Second pane content. */
  second: ReactNode;
  /** Initial split ratio (0-1, default 0.5). */
  initialRatio?: number;
  /** Minimum size in pixels for each pane. */
  minSize?: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SplitPane({
  direction,
  first,
  second,
  initialRatio = 0.5,
  minSize = 100,
}: SplitPaneProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [ratio, setRatio] = useState(initialRatio);
  const isDraggingRef = useRef(false);

  // Compute the axis-specific properties
  const isHorizontal = direction === "horizontal";

  // Start dragging
  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    isDraggingRef.current = true;
  }, []);

  // During drag
  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const totalSize = isHorizontal ? rect.width : rect.height;
      const offset = isHorizontal ? e.clientX - rect.left : e.clientY - rect.top;

      // Clamp to min/max
      const minRatio = minSize / totalSize;
      const maxRatio = 1 - minRatio;
      const newRatio = Math.max(minRatio, Math.min(maxRatio, offset / totalSize));

      setRatio(newRatio);
    },
    [isHorizontal, minSize],
  );

  // End drag
  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    isDraggingRef.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
  }, []);

  // Build styles for each pane
  const firstStyle = isHorizontal
    ? { width: `${ratio * 100}%`, height: "100%" }
    : { height: `${ratio * 100}%`, width: "100%" };

  const secondStyle = isHorizontal
    ? { width: `${(1 - ratio) * 100}%`, height: "100%" }
    : { height: `${(1 - ratio) * 100}%`, width: "100%" };

  const dividerClasses = isHorizontal
    ? "w-1 cursor-col-resize hover:bg-blue-500/30 active:bg-blue-500/50 transition-colors"
    : "h-1 cursor-row-resize hover:bg-blue-500/30 active:bg-blue-500/50 transition-colors";

  return (
    <div
      ref={containerRef}
      className={`flex ${isHorizontal ? "flex-row" : "flex-col"} flex-1 min-h-0 min-w-0 overflow-hidden`}
    >
      {/* First pane */}
      <div className="overflow-hidden min-h-0 min-w-0 flex flex-col" style={firstStyle}>
        {first}
      </div>

      {/* Divider */}
      <div
        className={`${dividerClasses} bg-zinc-700 shrink-0 z-10`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      />

      {/* Second pane */}
      <div className="overflow-hidden min-h-0 min-w-0 flex flex-col" style={secondStyle}>
        {second}
      </div>
    </div>
  );
}
